import { useMemo, useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { money, type Money } from "../../domain/money/money";
import { varianceRequiresAuthorization } from "../../domain/shift/expected-cash";
import type { Reading } from "../../domain/shift/reading";
import { Slot } from "../slots/Slot";

/**
 * Closing the drawer.
 *
 * The design rule here: never show a variance without showing what it is made
 * of. A staff member who is short and cannot see the components has no way to
 * find the mistake, and will start distrusting the till instead of looking.
 *
 * The counted amount is entered BEFORE the expected figure is revealed — seeing
 * the target first turns a count into a confirmation, which is exactly how a
 * shortage goes unnoticed.
 */

interface ShiftCloseProps {
  reading: Reading;
  currency: CurrencyFormat;
  varianceThreshold: Money;
  tenderLabels?: Record<string, string>;
  busy?: boolean;
  error?: string | null;
  onClose(input: { countedCash: Money; reason: string | null; authorizedBy: string | null }): void;
  onRequestManager(): Promise<string | null>;
  onCancel(): void;
}

const CASH_TENDER = "cash";

export function ShiftClose({
  reading,
  currency,
  varianceThreshold,
  tenderLabels = {},
  busy = false,
  error = null,
  onClose,
  onRequestManager,
  onCancel,
}: ShiftCloseProps) {
  const [entry, setEntry] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [reason, setReason] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState<string | null>(null);

  const counted = entry === "" ? 0 : Number.parseInt(entry, 10);
  const variance = counted - reading.expectedCash;
  const needsManager = useMemo(
    () => varianceRequiresAuthorization(money(variance), varianceThreshold),
    [variance, varianceThreshold],
  );

  const canClose = revealed && (!needsManager || (authorizedBy !== null && reason.trim() !== ""));

  // Non-cash tenders settle electronically — there's nothing to physically
  // count, so showing them before the cash count doesn't create the
  // count-into-confirmation bias the blind cash count guards against.
  const nonCashTenders = Object.entries(reading.tenderBreakdown).filter(
    ([type]) => type !== CASH_TENDER,
  );

  return (
    <div className="shiftclose" role="dialog" aria-label="Close the till">
      <header className="shiftclose__head">
        <h2 className="shiftclose__title">Close the till</h2>
        <p className="shiftclose__sub">
          {reading.transactionCount} sales · {formatMoney(reading.grossSales, currency)}
        </p>
      </header>

      {/* A module-contributed close-out summary — core renders it without
          knowing what's in it (Principle III). Empty with no module enabled,
          per SC-012. The peripherals module uses this to surface tickets that
          never printed (FR-217). */}
      <Slot id="till.shiftClose.summary" shiftId={reading.shiftId} />

      {!revealed ? (
        <section className="shiftclose__count">
          {nonCashTenders.length > 0 && (
            <dl className="shiftclose__breakdown shiftclose__breakdown--precount">
              {nonCashTenders.map(([type, amount]) => (
                <div key={type}>
                  <dt>{tenderLabels[type] ?? type}</dt>
                  <dd className="money">{formatMoney(amount as Money, currency)}</dd>
                </div>
              ))}
            </dl>
          )}

          <p className="shiftclose__prompt">Count the drawer and enter the total.</p>
          <div className="shiftclose__amount money">{formatMoney(counted as Money, currency)}</div>

          <div className="shiftclose__keys">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0"].map((key) => (
              <button
                key={key}
                type="button"
                className="shiftclose__key"
                onClick={() => setEntry((c) => (c + key).replace(/^0+(?=\d)/, "").slice(0, 9))}
              >
                {key}
              </button>
            ))}
            <button
              type="button"
              className="shiftclose__key shiftclose__key--muted"
              onClick={() => setEntry((c) => c.slice(0, -1))}
              aria-label="Delete"
            >
              ⌫
            </button>
          </div>

          <button
            type="button"
            className="shiftclose__reveal"
            disabled={entry === ""}
            onClick={() => setRevealed(true)}
          >
            Check the count
          </button>
        </section>
      ) : (
        <section className="shiftclose__result">
          {/* Components first. A variance you cannot explain is a variance you
              cannot fix. */}
          <dl className="shiftclose__breakdown">
            <div>
              <dt>Opening float</dt>
              <dd className="money">{formatMoney(reading.openingFloat, currency)}</dd>
            </div>
            {Object.entries(reading.tenderBreakdown).map(([type, amount]) => (
              <div key={type}>
                <dt>{tenderLabels[type] ?? type}</dt>
                <dd className="money">{formatMoney(amount as Money, currency)}</dd>
              </div>
            ))}
            <div className="shiftclose__breakdown-total">
              <dt>Expected in drawer</dt>
              <dd className="money">{formatMoney(reading.expectedCash, currency)}</dd>
            </div>
            <div>
              <dt>Counted</dt>
              <dd className="money">{formatMoney(counted as Money, currency)}</dd>
            </div>
          </dl>

          <div
            className={`shiftclose__variance${variance === 0 ? " is-exact" : variance < 0 ? " is-short" : " is-over"}`}
          >
            <span>{variance === 0 ? "Balanced" : variance < 0 ? "Short" : "Over"}</span>
            <span className="money">{formatMoney(Math.abs(variance) as Money, currency)}</span>
          </div>

          {needsManager && (
            <div className="shiftclose__auth">
              <p className="shiftclose__auth-note">
                This is beyond the tolerance. A manager needs to approve it, with a reason.
              </p>
              <input
                className="shiftclose__reason"
                placeholder="What happened?"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <button
                type="button"
                className="shiftclose__auth-button"
                onClick={async () => setAuthorizedBy(await onRequestManager())}
              >
                {authorizedBy ? "Approved" : "Get manager approval"}
              </button>
            </div>
          )}

          {error && (
            <p className="shiftclose__error" role="alert">
              {error}
            </p>
          )}

          <div className="shiftclose__actions">
            <button type="button" onClick={() => setRevealed(false)} disabled={busy}>
              Recount
            </button>
            <button
              type="button"
              className="is-primary"
              disabled={!canClose || busy}
              onClick={() =>
                onClose({
                  countedCash: money(counted),
                  reason: reason.trim() || null,
                  authorizedBy,
                })
              }
            >
              {busy ? "Closing…" : "Close the till"}
            </button>
          </div>
        </section>
      )}

      <footer className="shiftclose__foot">
        <button type="button" onClick={onCancel} disabled={busy}>
          Not yet
        </button>
      </footer>
    </div>
  );
}

export default ShiftClose;
